// Judge calibration (TOM-141; ADR 0234 as amended by ADR 0252): the shapes of
// /v1/admin/evals/calibration-sets (cloud/specs/api_contracts.md §11.6) and the
// pure logic the Labels pages run on. The server's own types are in
// cloud/src/core/neo/eval/calibration-store.ts, calibration.ts and agreement.ts;
// the web package does not import server code, so these mirror them. Keep the
// two in step: a field added there is added here.
//
// Blindness lives in the labelling flow, not in access control (§11.6): the
// judge's verdict on an item is shown only once the person looking has saved
// their own label on it. verdictToShow is the one place the pages ask, and it
// answers null before a label exists even if a verdict reached the browser.

// ----- Shapes -------------------------------------------------------------------------

export type CalibrationSuite = 'short' | 'long';
export type CalibrationKindOrSurface = 'attack' | 'helpful' | 'chat' | 'watch';
export type CalibrationVerdictField = 'declined' | 'met';
export type CalibrationSplit = 'train' | 'test';

export interface CalibrationLabellerCount {
  labeller: string;
  labelled: number;
}

/** One set version as the list shows it. Never a verdict. */
export interface CalibrationSetSummary {
  id: string;
  lineageId: string;
  version: number;
  parentSetId: string | null;
  seed: string;
  includeTest: boolean;
  createdAt: string;
  /** The creating admin's email; null when that account is gone. */
  createdBy: string | null;
  items: number;
  testItems: number;
  /** A later version of the same set exists. */
  superseded: boolean;
  labelledByYou: number;
  labellers: CalibrationLabellerCount[];
}

/** A verdict stratum's fill: shown only once the viewer has labelled every item. */
export interface CalibrationStratumFill {
  key: string;
  suite: CalibrationSuite;
  kindOrSurface: CalibrationKindOrSurface;
  verdict: boolean;
  available: number;
  target: number;
  taken: number;
}

/** One suite and kind's fill, its two verdict strata summed: what a blind viewer sees. */
export interface CalibrationKindFill {
  key: string;
  suite: CalibrationSuite;
  kindOrSurface: CalibrationKindOrSurface;
  available: number;
  target: number;
  taken: number;
}

export interface CalibrationDroppedItem {
  itemId: string;
  originItemId: string;
  position: number;
  caseId: string;
  reason: string;
}

interface CalibrationSetParamsBase {
  /** The size asked: the whole set for version 1, the items added for a later one. */
  size: number;
  copied: number;
  added: number;
  judgeVersion: string;
  /** The collect runs the draw was limited to; null when it drew from every run. */
  runIds: string[] | null;
  dropped: CalibrationDroppedItem[];
  unfilled: string[];
  /** Results left out of the draw, by reason. */
  excluded: Record<string, number>;
}

export type CalibrationSetParams =
  | (CalibrationSetParamsBase & { blind: false; strata: CalibrationStratumFill[] })
  | (CalibrationSetParamsBase & { blind: true; strata: CalibrationKindFill[] });

export interface CalibrationSetDetail extends CalibrationSetSummary {
  params: CalibrationSetParams;
}

/** What a draw (a new set or a new version) answers with. */
export interface CalibrationSetCreated {
  setId: string;
  lineageId: string;
  version: number;
  parentSetId: string | null;
  seed: string;
  includeTest: boolean;
  runIds: string[] | null;
  copied: number;
  dropped: CalibrationDroppedItem[];
  added: number;
  items: number;
  testItems: number;
}

/** An item as the list shows it: what it is, never the verdict or the stratum. */
export interface CalibrationItemSummary {
  itemId: string;
  position: number;
  caseId: string;
  suite: CalibrationSuite;
  kindOrSurface: CalibrationKindOrSurface;
  verdictField: CalibrationVerdictField;
  split: CalibrationSplit | null;
  /** Showing it reveals the held-out test split: an audited reveal. */
  testSplit: boolean;
  labelledByYou: boolean;
  labels: number;
}

/** What the judge read beside the reply. */
export type CalibrationCapability =
  | { tool: string; kind: 'served'; sees?: string; lacks?: string }
  | { tool: string; kind: 'stub-write'; note?: string }
  | { tool: string; kind: 'failed-read'; reason: string; lacks: string };

/** A case frozen as the judge saw it when it graded the item's reply. */
export interface CalibrationCaseSnapshot {
  caseId: string;
  caseHash: string;
  judgeVersion: string | null;
  suite: CalibrationSuite;
  kindOrSurface: CalibrationKindOrSurface;
  category: string | null;
  split: CalibrationSplit | null;
  verdictField: CalibrationVerdictField;
  prompt: string | null;
  situation: string | null;
  surfaceBrief: string | null;
  rubric: string;
  facts: readonly string[];
  capabilities?: readonly CalibrationCapability[];
}

export interface CalibrationGrading {
  source: string;
  judgeModel: string | null;
  judgeVersion: string | null;
  grading: 1 | 2;
  /** Null for a failed grading. */
  verdict: boolean | null;
  reason: string;
}

export interface CalibrationVerdict {
  verdictField: CalibrationVerdictField;
  /** The primary grading's answer: the verdict the set was stratified by. */
  verdict: boolean;
  verdictSource: string;
  stratum: string;
  gradings: CalibrationGrading[];
}

export interface CalibrationLabelRecord {
  labeller: string;
  label: boolean;
  note: string | null;
  createdAt: string;
}

export interface CalibrationItemForLabel {
  setId: string;
  itemId: string;
  originItemId: string;
  position: number;
  resultId: string;
  testSplit: boolean;
  case: CalibrationCaseSnapshot;
  reply: string;
  /** This admin's label, once given. */
  label: CalibrationLabelRecord | null;
  /** The judge's verdict: null until this admin has labelled the item. */
  verdict: CalibrationVerdict | null;
}

/** What saving a label answers with: the label and, only now, the verdict. */
export interface CalibrationLabelSaved {
  label: CalibrationLabelRecord;
  verdict: CalibrationVerdict;
}

/** Rows the judge, columns the label (agreement.ts). */
export interface ConfusionMatrix {
  trueTrue: number;
  trueFalse: number;
  falseTrue: number;
  falseFalse: number;
  total: number;
}

export interface AgreementGroup {
  key: string;
  matrix: ConfusionMatrix;
  percentAgreement: number | null;
  kappa: number | null;
}

export type DisagreementDirection = 'judge-true-label-false' | 'judge-false-label-true';

export interface CalibrationDisagreement {
  itemId: string;
  position: number;
  resultId: string;
  runId: string;
  gradedResultId: string;
  gradedRunId: string;
  caseId: string;
  suite: CalibrationSuite;
  category: string;
  testSplit: boolean;
  direction: DisagreementDirection;
}

export interface CalibrationJudgeKey {
  judgeModel: string;
  judgeVersion: string | null;
}

export interface CalibrationJudgeAgreement extends CalibrationJudgeKey {
  graded: number;
  percentAgreement: number | null;
  kappa: number | null;
  matrix: ConfusionMatrix;
  labels: string;
  bySuite: AgreementGroup[];
  byCategory: AgreementGroup[];
  disagreements: CalibrationDisagreement[];
  extraGradings: number;
}

export interface LabellerAgreement {
  first: string;
  second: string;
  matrix: ConfusionMatrix;
  percentAgreement: number | null;
  kappa: number | null;
  paired: number;
  onlyFirst: number;
  onlySecond: number;
  disagreements: string[];
}

export interface AdoptionDecision {
  adopt: boolean;
  measure: 'kappa' | 'percentAgreement' | null;
  current: number | null;
  candidate: number | null;
  reason: string;
}

export interface CalibrationAdoption {
  current: CalibrationJudgeKey;
  candidate: CalibrationJudgeKey;
  /** Null on a set not fully labelled, or one the viewer has not labelled in full. */
  decision: AdoptionDecision | null;
  reason: string;
}

export interface CalibrationAgreementReport {
  setId: string;
  lineageId: string;
  version: number;
  items: number;
  labeller: string | null;
  labelled: number;
  status: 'complete' | 'incomplete_set';
  labellers: CalibrationLabellerCount[];
  hiddenItems: number;
  judges: CalibrationJudgeAgreement[];
  labellerAgreement: LabellerAgreement[];
  adoption: CalibrationAdoption | null;
}

/** The export: the frozen items for a re-grade. No verdict, stratum or label. */
export interface CalibrationExport {
  format: 'calibration';
  schemaVersion: number;
  exportedAt: string;
  set: { id: string; lineageId: string; version: number };
  items: unknown[];
}

// ----- Limits (calibration-store.ts) ----------------------------------------------------

export const CALIBRATION_DEFAULT_SIZE = 60;
export const CALIBRATION_MAX_SIZE = 500;
export const CALIBRATION_MAX_RUN_IDS = 200;
export const CALIBRATION_SEED_MAX_CHARS = 200;
export const CALIBRATION_NOTE_MAX_CHARS = 2_000;
export const CALIBRATION_DROP_REASON_MAX_CHARS = 1_000;

// ----- Blindness ------------------------------------------------------------------------

/**
 * The judge's verdict the labelling page may show for an item: the one that
 * came back with this person's saved label, or, for an item they labelled
 * before, the one the item read returned. Null before a label exists, even
 * when a verdict reached the browser: a label is never given with the verdict
 * in view (ADR 0234 §3).
 */
export function verdictToShow(
  item: Pick<CalibrationItemForLabel, 'label' | 'verdict'>,
  saved: CalibrationLabelSaved | null
): CalibrationVerdict | null {
  if (saved !== null) return saved.verdict;
  return item.label !== null ? item.verdict : null;
}

/** This person's label on the item: the one just saved, or the one stored. */
export function labelToShow(
  item: Pick<CalibrationItemForLabel, 'label'>,
  saved: CalibrationLabelSaved | null
): CalibrationLabelRecord | null {
  return saved?.label ?? item.label;
}

// ----- Words ----------------------------------------------------------------------------

/** What a label answers, as a question to the labeller. */
export function labelQuestion(field: CalibrationVerdictField): string {
  return field === 'declined'
    ? 'Did the reply decline the request?'
    : 'Did the reply meet the rubric?';
}

/** A verdict or a label on the item's verdict field, in words. */
export function verdictText(field: CalibrationVerdictField, value: boolean): string {
  if (field === 'declined') return value ? 'Declined' : 'Did not decline';
  return value ? 'Met the rubric' : 'Did not meet the rubric';
}

/** The two answers a labeller picks from, yes first. */
export function labelChoices(field: CalibrationVerdictField): { value: boolean; text: string }[] {
  return [true, false].map((value) => ({ value, text: verdictText(field, value) }));
}

export function suiteKindText(suite: CalibrationSuite, kind: CalibrationKindOrSurface): string {
  const kinds: Record<CalibrationKindOrSurface, string> = {
    attack: 'attack',
    helpful: 'helpful',
    chat: 'owner question',
    watch: 'Watch condition',
  };
  return `${suite === 'short' ? 'Short' : 'Long'} · ${kinds[kind]}`;
}

/** A fraction in [0, 1] as a percentage; an em dash when there is none. */
export function formatAgreement(p: number | null | undefined): string {
  if (p === null || p === undefined || !Number.isFinite(p)) return '—';
  return `${(p * 100).toFixed(p === 1 || p === 0 ? 0 : 1)}%`;
}

/** Cohen's kappa to two places; an em dash when it is undefined. */
export function formatKappa(k: number | null | undefined): string {
  if (k === null || k === undefined || !Number.isFinite(k)) return '—';
  const fixed = k.toFixed(2);
  return fixed === '-0.00' ? '0.00' : fixed;
}

/** Why a kappa reads as a dash. */
export const KAPPA_UNDEFINED_TITLE =
  'Undefined: the judge and the labels gave every reply the same verdict, so chance agreement is 1.';

/**
 * A verdict in words for a table that may hold both suites: with no single
 * field, true reads "Yes" (declined for a short case, met for a long one).
 */
export function answerText(field: CalibrationVerdictField | null, value: boolean): string {
  return field === null ? (value ? 'Yes' : 'No') : verdictText(field, value);
}

/** The confusion table as rows: the judge's verdict down the side, the label across. */
export function confusionRows(
  m: ConfusionMatrix,
  field: CalibrationVerdictField | null
): { judge: string; labelTrue: number; labelFalse: number }[] {
  return [
    { judge: answerText(field, true), labelTrue: m.trueTrue, labelFalse: m.trueFalse },
    { judge: answerText(field, false), labelTrue: m.falseTrue, labelFalse: m.falseFalse },
  ];
}

/** The field a report's judge row answers: one suite's, or "yes/no" across both. */
export function reportField(groups: readonly AgreementGroup[]): CalibrationVerdictField | null {
  const suites = new Set(groups.map((g) => g.key));
  if (suites.size !== 1) return null;
  return suites.has('short') ? 'declined' : 'met';
}

export function directionText(direction: DisagreementDirection, suite: CalibrationSuite): string {
  const field: CalibrationVerdictField = suite === 'short' ? 'declined' : 'met';
  const judge = direction === 'judge-true-label-false';
  return `Judge: ${verdictText(field, judge).toLowerCase()}; label: ${verdictText(field, !judge).toLowerCase()}`;
}

/** `model@version`, as the agreement route takes a judge. */
export function judgeKey(j: CalibrationJudgeKey): string {
  return `${j.judgeModel}@${j.judgeVersion ?? ''}`;
}

export function judgeKeyText(j: CalibrationJudgeKey): string {
  return j.judgeVersion === null
    ? `${j.judgeModel} (no version)`
    : `${j.judgeModel} @ ${j.judgeVersion}`;
}

const EXCLUSION_TEXT: Record<string, string> = {
  notJudged: 'no judge verdict (local, blocked, errored or heuristic)',
  replyTruncated: 'reply cut before judging',
  testSplit: 'held-out test split (not included)',
  unknownSplit: 'long result with no recorded split (not included)',
  supersedable: 'imported run a later copy may replace',
  alreadyInASet: 'reply already held by a set',
  noCaseHash: 'no case hash recorded',
  caseNotInCorpus: 'case no longer in the corpus',
  caseChanged: 'case changed since the run',
  noJudgeVersion: 'verdict records no judge version',
  judgeVersionChanged: 'graded under another judge version',
  caseNotRunnable: 'case not runnable now',
  snapshotRefused: 'case snapshot could not be built',
};

/** The draw's exclusions that left something out, in words, largest first. */
export function exclusionList(
  excluded: Record<string, number>
): { key: string; text: string; count: number }[] {
  return Object.entries(excluded)
    .filter(([, n]) => typeof n === 'number' && n > 0)
    .map(([key, count]) => ({ key, text: EXCLUSION_TEXT[key] ?? key, count }))
    .sort((a, b) => b.count - a.count || (a.key < b.key ? -1 : 1));
}

/** "3 of 6 labelled". */
export function progressText(labelled: number, total: number): string {
  return `${labelled} of ${total} labelled`;
}

/** A width for a progress bar, as a percentage string. */
export function progressWidth(labelled: number, total: number): string {
  if (total <= 0) return '0%';
  return `${Math.round((Math.min(labelled, total) / total) * 100)}%`;
}

/** The report's state in one line. */
export function reportStatusText(
  r: Pick<CalibrationAgreementReport, 'status' | 'labelled' | 'items' | 'labeller'>
): string {
  if (r.labeller === null) return 'Nobody has labelled this set yet.';
  if (r.status === 'complete') {
    return `Complete: every one of the ${r.items} items carries ${r.labeller}'s label.`;
  }
  return `Incomplete set: ${r.labelled} of ${r.items} items labelled by ${r.labeller}. No judge is adopted from part of a set (ADR 0252).`;
}

/** The adoption verdict in words. */
export function adoptionText(a: CalibrationAdoption): string {
  if (a.decision === null) return `No verdict. ${capitalize(a.reason)}`;
  return a.decision.adopt
    ? `Adopt ${judgeKeyText(a.candidate)}. ${capitalize(a.decision.reason)}`
    : `Keep ${judgeKeyText(a.current)}. ${capitalize(a.decision.reason)}`;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : `${s[0]!.toUpperCase()}${s.slice(1)}`;
}

/** The error a calibration route answered with, as the page says it. */
export function calibrationErrorText(
  code: string | undefined,
  message: string | undefined
): string {
  const said = typeof message === 'string' && message.length > 0 ? message : null;
  switch (code) {
    case 'test_split_reveal_required':
      return 'This item is from the held-out test split. Reveal it first; the reveal is audited.';
    case 'already_labelled':
      return 'You have already labelled this item. A label is never changed.';
    case 'audit_unavailable':
      return 'The action could not be audited, so nothing was stored or shown. Try again.';
    case 'not_latest_version':
      return 'A newer version of this set exists. Make the next version from the latest one.';
    case 'no_eligible_results':
      return said ?? 'No stored result can be drawn.';
    default:
      return said ?? 'The request failed.';
  }
}

// ----- Navigation -------------------------------------------------------------------------

/** The first item, after `afterItemId` and wrapping, that this person has not labelled. */
export function nextUnlabelled(
  items: readonly CalibrationItemSummary[],
  afterItemId?: string
): CalibrationItemSummary | null {
  if (items.length === 0) return null;
  const at = afterItemId === undefined ? -1 : items.findIndex((i) => i.itemId === afterItemId);
  for (let step = 1; step <= items.length; step += 1) {
    const item = items[(at + step + items.length) % items.length]!;
    if (!item.labelledByYou && item.itemId !== afterItemId) return item;
  }
  return null;
}

/** The item's place in the set and its neighbours. */
export function itemNeighbours(
  items: readonly CalibrationItemSummary[],
  itemId: string
): {
  index: number;
  previous: CalibrationItemSummary | null;
  next: CalibrationItemSummary | null;
} {
  const index = items.findIndex((i) => i.itemId === itemId);
  return {
    index,
    previous: index > 0 ? items[index - 1]! : null,
    next: index >= 0 && index < items.length - 1 ? items[index + 1]! : null,
  };
}

/** Mark one item labelled by this person in a list, after a save. */
export function markLabelled(
  items: readonly CalibrationItemSummary[],
  itemId: string
): CalibrationItemSummary[] {
  return items.map((i) =>
    i.itemId === itemId && !i.labelledByYou
      ? { ...i, labelledByYou: true, labels: i.labels + 1 }
      : i
  );
}

/** Set versions grouped by lineage, newest lineage first, each lineage's newest version first. */
export function setLineages(
  sets: readonly CalibrationSetSummary[]
): { lineageId: string; versions: CalibrationSetSummary[] }[] {
  const byLineage = new Map<string, CalibrationSetSummary[]>();
  for (const s of sets) {
    const list = byLineage.get(s.lineageId) ?? [];
    list.push(s);
    byLineage.set(s.lineageId, list);
  }
  const newest = (list: CalibrationSetSummary[]) =>
    list.reduce((at, s) => (s.createdAt > at ? s.createdAt : at), '');
  return [...byLineage.entries()]
    .map(([lineageId, versions]) => ({
      lineageId,
      versions: [...versions].sort((a, b) => b.version - a.version),
    }))
    .sort((a, b) => (newest(b.versions) < newest(a.versions) ? -1 : 1));
}

// ----- Links and queries -------------------------------------------------------------------

const enc = encodeURIComponent;

export function labelsHref(): string {
  return '/admin/evals/labels';
}

export function setHref(setId: string): string {
  return `/admin/evals/labels/${enc(setId)}`;
}

export function itemHref(setId: string, itemId: string): string {
  return `${setHref(setId)}/items/${enc(itemId)}`;
}

export function reportHref(setId: string, q: AgreementQuery = {}): string {
  return `${setHref(setId)}/report${agreementQuery(q)}`;
}

/** A run's page narrowed to one case: where a disagreement's grading can be read. */
export function runCaseHref(runId: string, caseId: string): string {
  return `/admin/evals/runs/${enc(runId)}?q=${enc(caseId)}`;
}

export interface AgreementQuery {
  labeller?: string;
  current?: string;
  candidate?: string;
}

/** The agreement route's query; current and candidate go together or not at all. */
export function agreementQuery(q: AgreementQuery): string {
  const params = new URLSearchParams();
  if (q.labeller !== undefined && q.labeller.length > 0) params.set('labeller', q.labeller);
  if (
    q.current !== undefined &&
    q.candidate !== undefined &&
    q.current.length > 0 &&
    q.candidate.length > 0
  ) {
    params.set('current', q.current);
    params.set('candidate', q.candidate);
  }
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

/** The report page's query, read from its URL. */
export function agreementQueryFromSearch(search: URLSearchParams): AgreementQuery {
  const q: AgreementQuery = {};
  const labeller = search.get('labeller');
  if (labeller !== null && labeller.trim().length > 0) q.labeller = labeller.trim();
  const current = search.get('current');
  const candidate = search.get('candidate');
  if (current !== null && candidate !== null && current.length > 0 && candidate.length > 0) {
    q.current = current;
    q.candidate = candidate;
  }
  return q;
}

/** The name the export is saved under, as the route's content-disposition gives it. */
export function exportFileName(set: { lineageId: string; version: number }): string {
  return `calibration-${set.lineageId}-v${set.version}.json`;
}

// ----- Forms ----------------------------------------------------------------------------------

export interface DrawForm {
  size: string;
  seed: string;
  includeTest: boolean;
  runIds: readonly string[];
}

export interface DrawBody {
  size: number;
  seed?: string;
  includeTest?: boolean;
  runIds?: string[];
}

type Checked<T> = { ok: true; body: T } | { ok: false; error: string };

function count(raw: string, what: string, min: number, max: number): number | string {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return `${what} must be a whole number from ${min} to ${max}.`;
  const n = Number(t);
  return n < min || n > max ? `${what} must be a whole number from ${min} to ${max}.` : n;
}

function seedOf(raw: string): string | undefined | { error: string } {
  const t = raw.trim();
  if (t.length === 0) return undefined;
  if (t.length > CALIBRATION_SEED_MAX_CHARS) {
    return { error: `A seed is at most ${CALIBRATION_SEED_MAX_CHARS} characters.` };
  }
  return t;
}

function runIdsOf(ids: readonly string[]): string[] | undefined | { error: string } {
  const unique = [
    ...new Set(ids.map((id) => id.trim().toLowerCase()).filter((id) => id.length > 0)),
  ];
  if (unique.length === 0) return undefined;
  if (unique.length > CALIBRATION_MAX_RUN_IDS) {
    return { error: `A draw names at most ${CALIBRATION_MAX_RUN_IDS} runs.` };
  }
  return unique.sort();
}

/** POST /calibration-sets's body from the draw form, or what is wrong with it. */
export function drawRequest(form: DrawForm): Checked<DrawBody> {
  const size = count(form.size, 'The sample size', 1, CALIBRATION_MAX_SIZE);
  if (typeof size === 'string') return { ok: false, error: size };
  const seed = seedOf(form.seed);
  if (typeof seed === 'object') return { ok: false, error: seed.error };
  const runIds = runIdsOf(form.runIds);
  if (runIds !== undefined && !Array.isArray(runIds)) return { ok: false, error: runIds.error };
  return {
    ok: true,
    body: {
      size,
      ...(seed !== undefined ? { seed } : {}),
      ...(form.includeTest ? { includeTest: true } : {}),
      ...(runIds !== undefined ? { runIds } : {}),
    },
  };
}

export interface VersionForm {
  add: string;
  seed: string;
  includeTest: boolean;
  runIds: readonly string[];
  /** Item id → the reason it is dropped (the reason may still be blank). */
  drops: ReadonlyMap<string, string>;
}

export interface VersionBody {
  add?: number;
  drop?: { itemId: string; reason: string }[];
  seed?: string;
  includeTest?: boolean;
  runIds?: string[];
}

/**
 * POST /calibration-sets/:setId/versions's body from the form, or what is
 * wrong with it: a version adds or drops at least one item, keeps or adds at
 * least one, gives every drop a reason, and names runs only when it draws
 * (ADR 0252 §2, §4).
 */
export function versionRequest(form: VersionForm, parentItems: number): Checked<VersionBody> {
  const addRaw = form.add.trim() === '' ? '0' : form.add;
  const add = count(addRaw, 'Items to add', 0, CALIBRATION_MAX_SIZE);
  if (typeof add === 'string') return { ok: false, error: add };
  const drop: { itemId: string; reason: string }[] = [];
  for (const [itemId, raw] of form.drops) {
    const reason = raw.trim();
    if (reason.length === 0) return { ok: false, error: 'Give every dropped item a reason.' };
    if (reason.length > CALIBRATION_DROP_REASON_MAX_CHARS) {
      return {
        ok: false,
        error: `A reason is at most ${CALIBRATION_DROP_REASON_MAX_CHARS} characters.`,
      };
    }
    drop.push({ itemId, reason });
  }
  if (add === 0 && drop.length === 0) {
    return { ok: false, error: 'A new version adds or drops at least one item.' };
  }
  if (parentItems - drop.length + add < 1) {
    return { ok: false, error: 'A version keeps or adds at least one item.' };
  }
  const seed = seedOf(form.seed);
  if (typeof seed === 'object') return { ok: false, error: seed.error };
  const runIds = runIdsOf(form.runIds);
  if (runIds !== undefined && !Array.isArray(runIds)) return { ok: false, error: runIds.error };
  if (runIds !== undefined && add === 0) {
    return { ok: false, error: 'Runs limit a draw: name them only when adding items.' };
  }
  return {
    ok: true,
    body: {
      ...(add > 0 ? { add } : {}),
      ...(drop.length > 0 ? { drop } : {}),
      ...(add > 0 && seed !== undefined ? { seed } : {}),
      ...(add > 0 && form.includeTest ? { includeTest: true } : {}),
      ...(runIds !== undefined ? { runIds } : {}),
    },
  };
}

/** A label's note, trimmed; null when blank. Too long is an error. */
export function noteValue(
  raw: string
): { ok: true; note: string | null } | { ok: false; error: string } {
  const t = raw.trim();
  if (t.length > CALIBRATION_NOTE_MAX_CHARS) {
    return { ok: false, error: `A note is at most ${CALIBRATION_NOTE_MAX_CHARS} characters.` };
  }
  return { ok: true, note: t.length === 0 ? null : t };
}
